import { CheckCircle2, Wallet } from 'lucide-react'
import { formatAmount } from '@/shared/utils/formatAmount'
import { safeFormatDate } from '@/shared/utils/formatDate'
import { BillingStatusBadge } from './BillingStatusBadge'
import type { BillingDetail } from '../hooks/useBilling'

function buildCurrencyMap(billing: BillingDetail): Map<number, string> {
  const map = new Map<number, string>()
  if (billing.currency) map.set(billing.currency.id, billing.currency.name)
  return map
}

interface BillingInvoiceCardProps {
  billing: BillingDetail
}

/**
 * Factura detallada estilo documento real: cabecera con total y período (una
 * sola vez), tabla de líneas, subtotal/descuento/total y, en facturas con pagos,
 * el monto pagado y pendiente. El gateway no expone cantidad/precio unitario por
 * línea ni el nombre de la pasarela (solo `paymentGatewayId`).
 */
export function BillingInvoiceCard({ billing }: BillingInvoiceCardProps) {
  const currencyMap = buildCurrencyMap(billing)
  const cid = billing.currencyId ?? billing.currency?.id ?? null
  const money = (amount: number, currencyId?: number | null) =>
    formatAmount(amount, currencyId ?? cid, currencyMap)

  const primaryTotal =
    billing.totalPrices.find((t) => t.currencyId === billing.currencyId) ??
    billing.totalPrices[0]
  const totalText = primaryTotal ? money(primaryTotal.amount, primaryTotal.currencyId) : '—'

  const subtotal = billing.billingItems.reduce((sum, it) => sum + (it.amount ?? 0), 0)
  const discount = billing.discountAmount ?? 0
  const payments = billing.billingPayments ?? []
  const paidTotal = payments.reduce((sum, p) => sum + (p.amount ?? 0), 0)
  const status = billing.status?.toUpperCase() ?? ''
  const isPaid = status === 'PAID'

  return (
    <div className="overflow-hidden rounded-card border border-line bg-panel">
      {/* Cabecera */}
      <div className="relative overflow-hidden bg-sidebar p-6 text-white">
        <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-brand opacity-20 blur-2xl" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
                Factura
              </p>
              <p className="mt-1 font-serif text-2xl leading-none tracking-tight">
                {billing.invoiceNumber}
              </p>
            </div>
            <BillingStatusBadge status={billing.status} />
          </div>

          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
              {isPaid ? 'Total pagado' : 'Total a pagar'}
            </p>
            <p className="mt-1 font-serif text-[40px] font-normal leading-none tracking-tight">
              {totalText}
            </p>
          </div>

          {/* Período una sola vez + fechas */}
          <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-4 text-xs text-white/60">
            <div>
              Emitida
              <b className="mt-0.5 block text-[13px] font-semibold text-white">
                {safeFormatDate(billing.registeredAt, '—')}
              </b>
            </div>
            <div>
              Vence
              <b className="mt-0.5 block text-[13px] font-semibold text-amber-400">
                {billing.endsAt ? safeFormatDate(billing.endsAt) : 'Indefinida'}
              </b>
            </div>
            {billing.startsAt && billing.endsAt && (
              <div>
                Período facturado
                <b className="mt-0.5 block text-[13px] font-semibold text-white">
                  {safeFormatDate(billing.startsAt)} – {safeFormatDate(billing.endsAt)}
                </b>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Líneas */}
      <div className="p-6">
        {billing.billingItems.length === 0 ? (
          <p className="py-4 text-sm text-ink-muted">Esta factura no tiene líneas de detalle.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-[11px] font-bold uppercase tracking-[0.08em] text-ink-subtle">
                <th className="py-2 pr-2 font-bold">Concepto</th>
                <th className="py-2 px-2 text-center font-bold">Cant.</th>
                <th className="py-2 px-2 text-right font-bold">Precio</th>
                <th className="py-2 pl-2 text-right font-bold">Importe</th>
              </tr>
            </thead>
            <tbody>
              {billing.billingItems.map((item) => {
                // El `PortalBillingItem` del gateway no tiene cantidad ni precio
                // unitario: cada línea es un importe único.
                const qty = 1
                const unit = item.amount / qty
                return (
                  <tr key={item.id} className="border-b border-line/70 last:border-b-0">
                    <td className="py-2.5 pr-2 font-medium text-ink">{item.description}</td>
                    <td className="py-2.5 px-2 text-center tabular-nums text-ink-muted">{qty}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums text-ink-muted">
                      {money(unit, item.currencyId)}
                    </td>
                    <td className="py-2.5 pl-2 text-right font-semibold tabular-nums text-ink">
                      {money(item.amount, item.currencyId)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Totales */}
        <div className="mt-4 ml-auto max-w-xs space-y-1.5 text-sm">
          <div className="flex items-center justify-between text-ink-muted">
            <span>Subtotal</span>
            <span className="tabular-nums">{money(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-ink-muted">Descuento</span>
              <span className="font-medium tabular-nums text-success">- {money(discount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t-2 border-ink pt-2 text-[15px] font-bold text-ink">
            <span>Total</span>
            <span className="tabular-nums">{totalText}</span>
          </div>
        </div>

        {/* Método de pago (pasarela) — facturas con pagos */}
        {payments.length > 0 && (
          <div className="mt-5 rounded-xl border border-line bg-surface p-4">
            <div className="flex items-center gap-2">
              {isPaid ? (
                <CheckCircle2 className="h-4 w-4 text-success" />
              ) : (
                <Wallet className="h-4 w-4 text-brand" />
              )}
              <p className="text-sm font-semibold text-ink">
                {isPaid ? 'Pagada' : 'Pago parcial registrado'}
              </p>
            </div>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-ink-muted">Monto pagado</span>
                <span className="font-medium tabular-nums text-ink">{money(paidTotal)}</span>
              </div>
              {!isPaid && primaryTotal && paidTotal < primaryTotal.amount && (
                <div className="flex items-center justify-between">
                  <span className="text-ink-muted">Pendiente</span>
                  <span className="font-semibold tabular-nums text-warning">
                    {money(primaryTotal.amount - paidTotal)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Totales en otras monedas (si los hubiera) */}
        {billing.totalPrices.length > 1 && (
          <div className="mt-3 space-y-1">
            {billing.totalPrices
              .filter((t) => t !== primaryTotal)
              .map((t) => (
                <div key={t.id} className="flex items-center justify-between text-xs text-ink-muted">
                  <span>Total ({currencyMap.get(t.currencyId) ?? t.currencyId})</span>
                  <span className="tabular-nums">{money(t.amount, t.currencyId)}</span>
                </div>
              ))}
          </div>
        )}

        {billing.observations && (
          <p className="mt-4 rounded-lg bg-surface p-3 text-xs text-ink-muted">
            {billing.observations}
          </p>
        )}
      </div>
    </div>
  )
}
